// src/dataStore.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, BASE_API_URL, toMediaUrl } from "./api";
import { DEFAULT_BRAND_THEME, normalizeBrandTheme } from "./themeTokens";
import {
  FALLBACK_LANGUAGE_CODE,
  LANGUAGE_STORAGE_KEY,
  applyEntityTranslation,
  createTranslator,
  filterSupportedLanguages,
  findLanguage,
  getAllergenTranslation,
  getLanguageCode,
  getLanguageIref,
  getTextConfigValue,
  resolveDefaultLanguage,
  resolveSelectedLanguage,
} from "./i18n";

const DataStoreContext = createContext(null);

function getFrontConfigValue(data, key) {
  if (!data || !key) return "";

  const fromArray = (list) => {
    if (!Array.isArray(list)) return "";
    return String(list.find((x) => x?.key === key)?.value || "").trim();
  };

  if (Array.isArray(data)) return fromArray(data);

  const platformValue = fromArray(data.platform);
  if (platformValue) return platformValue;

  if (data.platform && typeof data.platform === "object") {
    return String(data.platform[key] || "").trim();
  }

  const directValue = data[key];
  if (directValue && typeof directValue === "object") {
    return String(directValue.value || "").trim();
  }

  return String(directValue || data.value || "").trim();
}

function getFirstFrontConfigValue(data, keys) {
  return keys.map((key) => getFrontConfigValue(data, key)).find(Boolean) || "";
}

function getTextConfigsByKey(data) {
  const list = Array.isArray(data)
    ? data
    : data?.MobileAppTextConfigs || data?.results || data?.data || data?.items || [];
  if (!Array.isArray(list)) return {};

  return list.reduce((acc, item) => {
    const key = String(item?.key || "").trim();
    if (!key) return acc;

    acc[key] = [...(acc[key] || []), item];
    return acc;
  }, {});
}

function getTextConfigByKey(configsByKey, key, selectedLanguage, defaultLanguage) {
  return String(
    getTextConfigValue(configsByKey?.[key], "", selectedLanguage, defaultLanguage) || ""
  ).trim();
}

function getFirstTextConfigValue(configsByKey, keys, selectedLanguage, defaultLanguage) {
  return (
    keys
      .map((key) => getTextConfigByKey(configsByKey, key, selectedLanguage, defaultLanguage))
      .find(Boolean) || ""
  );
}

const WEBSITE_ENABLED_VALUES = new Set(["1", "true", "yes", "on"]);
const WEBSITE_DISABLED_VALUES = new Set(["0", "false", "no", "off"]);

export function parseWebsiteContentEnabled(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (value === 1) return true;
  if (value === 0) return false;

  const normalized = String(value ?? "").trim().toLowerCase();
  if (WEBSITE_ENABLED_VALUES.has(normalized)) return true;
  if (WEBSITE_DISABLED_VALUES.has(normalized)) return false;
  return fallback;
}

export function normalizeWebsiteContentUrl(value) {
  const url = String(value || "").trim();
  if (!url || /[\s\\]/.test(url)) return "";
  if (/^\/(?!\/)/.test(url) || /^#[a-z0-9][a-z0-9:._-]*$/i.test(url)) return url;

  try {
    return new URL(url).protocol === "https:" ? url : "";
  } catch (e) {
    return "";
  }
}

export function normalizeHttpsUrl(value) {
  const url = String(value || "").trim();
  if (!url || /\s/.test(url)) return "";

  try {
    return new URL(url).protocol === "https:" ? url : "";
  } catch (e) {
    return "";
  }
}

function getWebsiteAction(configsByKey, prefix, selectedLanguage, defaultLanguage) {
  const label = getTextConfigByKey(
    configsByKey,
    `${prefix}_LABEL`,
    selectedLanguage,
    defaultLanguage
  );
  const url = normalizeWebsiteContentUrl(
    getTextConfigByKey(
      configsByKey,
      `${prefix}_URL`,
      selectedLanguage,
      defaultLanguage
    )
  );

  return label && url
    ? { label, url }
    : { label: "", url: "" };
}

function resolveWebsiteSectionEnabled(configValue, hasContent) {
  if (!hasContent) return false;
  if (!String(configValue || "").trim()) return true;
  return parseWebsiteContentEnabled(configValue, true);
}

function resolveWebsiteHeroPriority(value, slot) {
  const rawValue = String(value ?? "").trim();
  if (!rawValue) return slot;

  const priority = Number(rawValue);
  return Number.isFinite(priority) ? priority : slot;
}

export function buildWebsiteContent({
  configsByKey = {},
  selectedLanguage,
  defaultLanguage,
  bannerUrl = "",
  images = {},
} = {}) {
  const text = (key) =>
    getTextConfigByKey(configsByKey, key, selectedLanguage, defaultLanguage);

  const legacyHeroPrimary = getWebsiteAction(
    configsByKey,
    "WEBSITE_HERO_PRIMARY",
    selectedLanguage,
    defaultLanguage
  );
  const legacyHeroSecondary = getWebsiteAction(
    configsByKey,
    "WEBSITE_HERO_SECONDARY",
    selectedLanguage,
    defaultLanguage
  );
  const legacyHeroImage = String(bannerUrl || "").trim();
  const legacyHero = {
    enabled: true,
    slot: 0,
    priority: 0,
    title: text("WEBSITE_HERO_TITLE"),
    body: text("WEBSITE_HERO_BODY"),
    primaryLabel: legacyHeroPrimary.label,
    primaryUrl: legacyHeroPrimary.url,
    secondaryLabel: legacyHeroSecondary.label,
    secondaryUrl: legacyHeroSecondary.url,
    imageAlt: text("WEBSITE_HERO_IMAGE_ALT"),
    image: legacyHeroImage,
    mobileImage: legacyHeroImage,
  };

  const numberedHeroes = [1, 2, 3]
    .map((slot) => {
      const prefix = `WEBSITE_HERO_${slot}`;
      const title = text(`${prefix}_TITLE`);
      const image = String(images[`hero${slot}`] || "").trim();
      const primary = getWebsiteAction(
        configsByKey,
        `${prefix}_PRIMARY`,
        selectedLanguage,
        defaultLanguage
      );
      const secondary = getWebsiteAction(
        configsByKey,
        `${prefix}_SECONDARY`,
        selectedLanguage,
        defaultLanguage
      );
      const enabled = resolveWebsiteSectionEnabled(
        text(`${prefix}_ENABLED`),
        Boolean(title && image)
      );

      return {
        enabled,
        slot,
        priority: resolveWebsiteHeroPriority(
          text(`${prefix}_PRIORITY`),
          slot
        ),
        title,
        body: text(`${prefix}_BODY`),
        primaryLabel: primary.label,
        primaryUrl: primary.url,
        secondaryLabel: secondary.label,
        secondaryUrl: secondary.url,
        imageAlt: text(`${prefix}_IMAGE_ALT`),
        image,
        mobileImage:
          String(images[`hero${slot}Mobile`] || "").trim() || image,
      };
    })
    .filter((hero) => hero.enabled)
    .sort((a, b) => a.priority - b.priority || a.slot - b.slot);

  const features = [1, 2].map((slot) => {
    const prefix = `WEBSITE_FEATURE_${slot}`;
    const title = text(`${prefix}_TITLE`);
    const body = text(`${prefix}_BODY`);
    const image = String(images[`feature${slot}`] || "").trim();
    const action = getWebsiteAction(
      configsByKey,
      `${prefix}_CTA`,
      selectedLanguage,
      defaultLanguage
    );
    const hasContent = Boolean(title && image);

    return {
      slot,
      enabled: resolveWebsiteSectionEnabled(text(`${prefix}_ENABLED`), hasContent),
      title,
      body,
      ctaLabel: action.label,
      ctaUrl: action.url,
      imageAlt: text(`${prefix}_IMAGE_ALT`),
      image,
    };
  });

  const storyAction = getWebsiteAction(
    configsByKey,
    "WEBSITE_STORY_CTA",
    selectedLanguage,
    defaultLanguage
  );
  const story = {
    title: text("WEBSITE_STORY_TITLE"),
    body: text("WEBSITE_STORY_BODY"),
    ctaLabel: storyAction.label,
    ctaUrl: storyAction.url,
    imageAlt: text("WEBSITE_STORY_IMAGE_ALT"),
    image: String(images.story || "").trim(),
  };
  story.enabled = resolveWebsiteSectionEnabled(
    text("WEBSITE_STORY_ENABLED"),
    Boolean(story.title && story.image)
  );

  return {
    heroes: numberedHeroes,
    hero: numberedHeroes[0] || legacyHero,
    features,
    story,
    menu: {
      title: text("WEBSITE_MENU_TITLE"),
      body: text("WEBSITE_MENU_BODY"),
    },
    locations: {
      title: text("WEBSITE_LOCATIONS_TITLE"),
      body: text("WEBSITE_LOCATIONS_BODY"),
    },
    app: {
      title: text("WEBSITE_APP_TITLE"),
      body: text("WEBSITE_APP_BODY"),
      image: String(images.appPromo || "").trim(),
    },
    footer: {
      text: text("WEBSITE_FOOTER_TEXT"),
      instagramUrl: normalizeWebsiteContentUrl(text("WEBSITE_INSTAGRAM_URL")),
      facebookUrl: normalizeWebsiteContentUrl(text("WEBSITE_FACEBOOK_URL")),
    },
  };
}

export function resolveWebsiteContentImages(data, resolveMediaUrl = toMediaUrl) {
  const list = Array.isArray(data)
    ? data
    : data?.MobileAppImageConfigs || data?.results || data?.data || data?.items || [];
  const findImage = (key) => {
    if (!Array.isArray(list)) return "";
    const path = list.find((item) => item?.key === key)?.image || "";
    return path ? resolveMediaUrl(path) : "";
  };

  return {
    hero1: findImage("WEBSITE_HERO_1_IMAGE"),
    hero1Mobile: findImage("WEBSITE_HERO_1_MOBILE_IMAGE"),
    hero2: findImage("WEBSITE_HERO_2_IMAGE"),
    hero2Mobile: findImage("WEBSITE_HERO_2_MOBILE_IMAGE"),
    hero3: findImage("WEBSITE_HERO_3_IMAGE"),
    hero3Mobile: findImage("WEBSITE_HERO_3_MOBILE_IMAGE"),
    feature1: findImage("WEBSITE_FEATURE_1_IMAGE"),
    feature2: findImage("WEBSITE_FEATURE_2_IMAGE"),
    story: findImage("WEBSITE_STORY_IMAGE"),
    appPromo: findImage("WEBSITE_APP_PROMO_IMAGE"),
  };
}

export function resolveLocalizedStoreBadges(
  images = {},
  languageCode = FALLBACK_LANGUAGE_CODE
) {
  const requestedLanguage = String(
    languageCode || FALLBACK_LANGUAGE_CODE
  ).trim().toUpperCase();
  const normalizedLanguage = ["RO", "EN"].includes(requestedLanguage)
    ? requestedLanguage
    : "";

  return {
    ...images,
    appStoreBadge:
      (normalizedLanguage
        ? images[`appStoreBadge${normalizedLanguage}`]
        : "") ||
      images.appStoreBadge ||
      "",
    playStoreBadge:
      (normalizedLanguage
        ? images[`playStoreBadge${normalizedLanguage}`]
        : "") ||
      images.playStoreBadge ||
      "",
  };
}

function getStoredLanguageIref() {
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) || "";
  } catch (e) {
    return "";
  }
}

function getUrlLanguageValue() {
  try {
    const params = new URLSearchParams(window.location.search);
    return String(params.get("language") || params.get("lang") || "").trim();
  } catch (e) {
    return "";
  }
}

function getStoredFrontConfig() {
  try {
    const rawConfig = window.localStorage.getItem("config");
    return rawConfig ? JSON.parse(rawConfig) : null;
  } catch (e) {
    return null;
  }
}

function getStoredAccessToken() {
  try {
    const rawUser = window.localStorage.getItem("apiUser");
    const data = rawUser ? JSON.parse(rawUser) : null;
    const token = String(data?.accessToken || "").trim();
    if (!token) return "";

    const payload = JSON.parse(window.atob(token.split(".")[1] || ""));
    if (payload?.exp && Date.now() >= payload.exp * 1000) return "";

    return token;
  } catch (e) {
    return "";
  }
}

async function loadAuthenticatedFrontConfig() {
  const token = getStoredAccessToken();
  if (!token) return null;

  const res = await fetch(`${BASE_API_URL}config/getConfigFront/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) return null;
  return res.json();
}

function storeLanguageIref(languageIref) {
  try {
    if (languageIref) {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, languageIref);
    } else {
      window.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    }
  } catch (e) {
    // Storage can be unavailable in embedded/private contexts.
  }
}

export function resolveProductAllergens(product, translatedAllergens = []) {
  const allergenById = new Map(
    (translatedAllergens || []).map((allergen) => [
      String(allergen.iref),
      allergen,
    ])
  );
  const list = Array.isArray(product?.allergens)
    ? product.allergens
    : product?.allergens
      ? [product.allergens]
      : [];

  return list
    .map((item) => {
      const id =
        typeof item === "object"
          ? item.iref || item.id || item.allergen || item.alergen
          : item;

      return allergenById.get(String(id));
    })
    .filter(Boolean);
}

export function DataStoreProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [coreLoadVersion, setCoreLoadVersion] = useState(0);
  const [publicLoadVersion, setPublicLoadVersion] = useState(0);

  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allergens, setAllergens] = useState([]);

  const [languages, setLanguages] = useState([]);
  const [defaultLanguageValue, setDefaultLanguageValue] = useState("");
  const [selectedLanguageIref, setSelectedLanguageIrefState] = useState(
    () => getUrlLanguageValue() || getStoredLanguageIref()
  );
  const [languageReady, setLanguageReady] = useState(false);
  const [publicConfigReady, setPublicConfigReady] = useState(false);
  const [publicImagesReady, setPublicImagesReady] = useState(false);
  const [publicTextConfigError, setPublicTextConfigError] = useState(false);
  const [publicImageConfigError, setPublicImageConfigError] = useState(false);

  const [logoUrl, setLogoUrl] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [mobileTextConfigs, setMobileTextConfigs] = useState({});
  const [appStoreUrl, setAppStoreUrl] = useState("");
  const [playStoreUrl, setPlayStoreUrl] = useState("");
  const [appDownloadImages, setAppDownloadImages] = useState({});
  const [websiteImages, setWebsiteImages] = useState({});
  const [brandTheme, setBrandTheme] = useState(DEFAULT_BRAND_THEME);

  const defaultLanguage = useMemo(
    () => resolveDefaultLanguage(languages, defaultLanguageValue),
    [languages, defaultLanguageValue]
  );

  const selectedLanguage = useMemo(
    () => resolveSelectedLanguage(languages, selectedLanguageIref, defaultLanguage),
    [languages, selectedLanguageIref, defaultLanguage]
  );

  const languageCode = useMemo(
    () => getLanguageCode(selectedLanguage || defaultLanguage),
    [selectedLanguage, defaultLanguage]
  );

  const t = useMemo(() => createTranslator(languageCode), [languageCode]);

  useEffect(() => {
    const normalizedLanguageCode =
      String(languageCode || FALLBACK_LANGUAGE_CODE).trim().toLowerCase() ||
      FALLBACK_LANGUAGE_CODE.toLowerCase();

    document.documentElement.lang = normalizedLanguageCode;
  }, [languageCode]);

  const setSelectedLanguageIref = useCallback(
    (languageIref) => {
      const next = String(languageIref || "").trim();
      const resolved = findLanguage(languages, next);
      const finalIref = getLanguageIref(resolved) || next;

      setSelectedLanguageIrefState(finalIref);
      storeLanguageIref(finalIref);
    },
    [languages]
  );

  useEffect(() => {
    if (!languageReady || !languages.length) return;

    const selectedExists = Boolean(findLanguage(languages, selectedLanguageIref));
    if (selectedExists) return;

    const fallbackIref = getLanguageIref(defaultLanguage) || getLanguageIref(languages[0]);
    if (!fallbackIref) return;

    setSelectedLanguageIrefState(fallbackIref);
    storeLanguageIref(fallbackIref);
  }, [languageReady, languages, selectedLanguageIref, defaultLanguage]);

  // Load language list before API calls that support language filtering.
  useEffect(() => {
    let mounted = true;

    async function loadLanguages() {
      let publicConfigSucceeded = false;

      try {
        const cachedFrontConfig = getStoredFrontConfig();
        const [languageResult, textConfigResult] = await Promise.all([
          api.getLanguages().then(
            (data) => ({ ok: true, data }),
            () => ({ ok: false, data: null })
          ),
          fetch(`${BASE_API_URL}config_mapp_text/`)
            .then((res) => {
              if (!res.ok) throw new Error("Mobile Identity text config failed");
              return res.json();
            })
            .then(
              (data) => ({ ok: true, data }),
              () => ({ ok: false, data: null })
            ),
        ]);

        if (!mounted) return;

        if (languageResult.ok) {
          const nextLanguages = filterSupportedLanguages(
            Array.isArray(languageResult.data?.Languages)
              ? languageResult.data.Languages
              : []
          );
          const queryLanguage = findLanguage(
            nextLanguages,
            getUrlLanguageValue()
          );
          const queryLanguageIref = getLanguageIref(queryLanguage);

          setLanguages(nextLanguages);

          if (queryLanguageIref) {
            setSelectedLanguageIrefState(queryLanguageIref);
            storeLanguageIref(queryLanguageIref);
          }
        }

        let defaultLanguageFromText = "";
        if (textConfigResult.ok) {
          const nextMobileTextConfigs = getTextConfigsByKey(
            textConfigResult.data
          );
          defaultLanguageFromText = getTextConfigByKey(
            nextMobileTextConfigs,
            "LANGUAGE"
          );
          setMobileTextConfigs(nextMobileTextConfigs);
        }

        const cachedDefaultLanguage = getFrontConfigValue(
          cachedFrontConfig,
          "LANGUAGE"
        );
        if (defaultLanguageFromText || cachedDefaultLanguage) {
          setDefaultLanguageValue(
            (current) =>
              defaultLanguageFromText || current || cachedDefaultLanguage
          );
        }

        publicConfigSucceeded = languageResult.ok && textConfigResult.ok;
      } finally {
        if (mounted) {
          setPublicTextConfigError(!publicConfigSucceeded);
          setLanguageReady(true);
          setPublicConfigReady(true);
        }
      }
    }

    loadLanguages();
    return () => {
      mounted = false;
    };
  }, [publicLoadVersion]);

  // Load core data (cached once)
  useEffect(() => {
    if (!languageReady) return undefined;
    const requestLanguageIref = getLanguageIref(selectedLanguage);
    if (languages.length && !requestLanguageIref) return undefined;

    let mounted = true;

    async function loadCore() {
      try {
        setLoading(true);
        setError(false);

        const [pRes, lRes, cRes, aRes] = await Promise.all([
          api.getProducts(requestLanguageIref), // { products: [...] }
          api.getLocations(), // { locations: [...] }
          api.getCategoriesWithSub(requestLanguageIref), // { productCategories_withSubCategories: [...] }
          api.getAllergens(requestLanguageIref).catch(() => null),
        ]);

        if (!mounted) return;

        setProducts(pRes?.products || []);
        setLocations(lRes?.locations || []);
        setCategories(cRes?.productCategories_withSubCategories || []);
        setAllergens(aRes?.allergens || []);
      } catch (e) {
        if (!mounted) return;
        setError(true);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    loadCore();
    return () => {
      mounted = false;
    };
  }, [
    coreLoadVersion,
    languageReady,
    languages.length,
    selectedLanguage,
  ]);

  const retryPublicContent = useCallback(() => {
    setPublicLoadVersion((value) => value + 1);
  }, []);

  const retryCore = useCallback(() => {
    setCoreLoadVersion((value) => value + 1);
    retryPublicContent();
  }, [retryPublicContent]);

  // Load logo (independent, does not block the app)
  useEffect(() => {
    let mounted = true;

    async function loadLogo() {
      try {
        const res = await fetch(`${BASE_API_URL}config_mapp_image/?key=LOGO`);
        const data = await res.json();
        if (!mounted) return;
        const list = Array.isArray(data) ? data : data?.MobileAppImageConfigs || [];
        const item = Array.isArray(list)
          ? list.find((x) => x?.key === "LOGO")
          : null;
        const path = item?.image || "";
        if (!path) return;

        setLogoUrl(toMediaUrl(path));
      } catch (e) {
        // ignore: logo is optional
      }
    }

    loadLogo();
    return () => {
      mounted = false;
    };
  }, []);

  //Load banner
  useEffect(() => {
    let mounted = true;

    async function loadMobileAppImages() {
      let publicImagesSucceeded = false;

      try {
        const res = await fetch(`${BASE_API_URL}config_mapp_image/`);
        if (!res.ok) throw new Error("Mobile Identity image config failed");

        const data = await res.json();
        if (!mounted) return;

        const list = Array.isArray(data) ? data : data?.MobileAppImageConfigs || [];
        if (!Array.isArray(list)) {
          throw new Error("Mobile Identity image config is malformed");
        }

        const findByKey = (key) => list.find((x) => x?.key === key);
        const findImageByKeys = (keys) => {
          const item = keys.map(findByKey).find((x) => x?.image);
          return item?.image ? toMediaUrl(item.image) : "";
        };

        const logoPath = findByKey("LOGO")?.image || "";
        const bannerPath = findByKey("BANNER")?.image || "";

        if (logoPath) {
            setLogoUrl(toMediaUrl(logoPath));
        }

        if (bannerPath) {
            setBannerUrl(toMediaUrl(bannerPath));
        }

        const imageConfigAssets = {
          background: findImageByKeys(["APP_DOWNLOAD_BACKGROUND", "APP_BACKGROUND", "APP_BG"]),
          screen1: findImageByKeys(["APP_DOWNLOAD_SCREEN_1", "APP_SCREEN_1", "APP1"]),
          screen2: findImageByKeys(["APP_DOWNLOAD_SCREEN_2", "APP_SCREEN_2", "APP2"]),
          screen3: findImageByKeys(["APP_DOWNLOAD_SCREEN_3", "APP_SCREEN_3", "APP3"]),
          appStoreBadge: findImageByKeys(["APPSTORE_BADGE", "APP_STORE_BADGE"]),
          appStoreBadgeRO: findImageByKeys(["APPSTORE_BADGE_RO"]),
          appStoreBadgeEN: findImageByKeys(["APPSTORE_BADGE_EN"]),
          playStoreBadge: findImageByKeys(["PLAYSTORE_BADGE", "PLAY_STORE_BADGE", "GOOGLEPLAY_BADGE"]),
          playStoreBadgeRO: findImageByKeys(["PLAYSTORE_BADGE_RO"]),
          playStoreBadgeEN: findImageByKeys(["PLAYSTORE_BADGE_EN"]),
        };
        const websiteConfigAssets = resolveWebsiteContentImages(list);

        setAppDownloadImages((prev) => {
          const next = { ...prev };
          Object.keys(imageConfigAssets).forEach((key) => {
            if (imageConfigAssets[key] && !next[key]) {
              next[key] = imageConfigAssets[key];
            }
          });
          return next;
        });
        setWebsiteImages(websiteConfigAssets);
        publicImagesSucceeded = true;
      } catch (e) {
        // ignore
      } finally {
        if (mounted) {
          setPublicImageConfigError(!publicImagesSucceeded);
          setPublicImagesReady(true);
        }
      }
    }

    loadMobileAppImages();
    return () => {
        mounted = false;
    };
  }, [publicLoadVersion]);

  // Apply public text config values.
  useEffect(() => {
    const appUrl = getTextConfigByKey(
      mobileTextConfigs,
      "APPSTORE_URL",
      selectedLanguage,
      defaultLanguage
    );
    const playUrl = getTextConfigByKey(
      mobileTextConfigs,
      "PLAYSTORE_URL",
      selectedLanguage,
      defaultLanguage
    );
    const defaultLanguageConfig = getTextConfigByKey(
      mobileTextConfigs,
      "LANGUAGE",
      selectedLanguage,
      defaultLanguage
    );
    const textTheme = {
      primary: getFirstTextConfigValue(
        mobileTextConfigs,
        ["BRAND_PRIMARY_COLOR", "BRAND_ACCENT_COLOR"],
        selectedLanguage,
        defaultLanguage
      ),
      accent: getFirstTextConfigValue(
        mobileTextConfigs,
        ["BRAND_MUTED_COLOR", "BRAND_SECONDARY_COLOR"],
        selectedLanguage,
        defaultLanguage
      ),
      cta: getFirstTextConfigValue(
        mobileTextConfigs,
        ["BRAND_CTA_COLOR", "BRAND_ACTION_COLOR"],
        selectedLanguage,
        defaultLanguage
      ),
      background: getFirstTextConfigValue(
        mobileTextConfigs,
        ["BRAND_BACKGROUND_COLOR", "BRAND_PAGE_BACKGROUND_COLOR"],
        selectedLanguage,
        defaultLanguage
      ),
      surface: getFirstTextConfigValue(
        mobileTextConfigs,
        ["BRAND_SURFACE_COLOR", "BRAND_CARD_COLOR"],
        selectedLanguage,
        defaultLanguage
      ),
      text: getFirstTextConfigValue(
        mobileTextConfigs,
        ["BRAND_TEXT_COLOR", "BRAND_HEADING_COLOR"],
        selectedLanguage,
        defaultLanguage
      ),
      muted: getFirstTextConfigValue(
        mobileTextConfigs,
        ["BRAND_MUTED_TEXT_COLOR", "BRAND_SUPPORTING_COLOR"],
        selectedLanguage,
        defaultLanguage
      ),
      dark: getFirstTextConfigValue(
        mobileTextConfigs,
        ["BRAND_DARK_COLOR", "BRAND_FOOTER_COLOR"],
        selectedLanguage,
        defaultLanguage
      ),
      onPrimary: getFirstTextConfigValue(
        mobileTextConfigs,
        ["BRAND_ON_PRIMARY_COLOR", "BRAND_PRIMARY_TEXT_COLOR"],
        selectedLanguage,
        defaultLanguage
      ),
      onCta: getFirstTextConfigValue(
        mobileTextConfigs,
        ["BRAND_ON_CTA_COLOR", "BRAND_CTA_TEXT_COLOR"],
        selectedLanguage,
        defaultLanguage
      ),
    };

    if (defaultLanguageConfig) setDefaultLanguageValue(defaultLanguageConfig);
    const safeAppUrl = normalizeHttpsUrl(appUrl);
    const safePlayUrl = normalizeHttpsUrl(playUrl);
    if (safeAppUrl) setAppStoreUrl(safeAppUrl);
    if (safePlayUrl) setPlayStoreUrl(safePlayUrl);
    setBrandTheme((prev) => normalizeBrandTheme(textTheme, prev));
  }, [mobileTextConfigs, selectedLanguage, defaultLanguage]);

  // Load store links from cached/authenticated front-exposed config.
  useEffect(() => {
    let mounted = true;

    function applyFrontConfig(data) {
      if (!data) return;

      const appUrl = getFrontConfigValue(data, "APPSTORE_URL");
      const playUrl = getFrontConfigValue(data, "PLAYSTORE_URL");
      const frontTheme = {
        primary: getFirstFrontConfigValue(data, ["BRAND_PRIMARY_COLOR", "BRAND_ACCENT_COLOR"]),
        accent: getFirstFrontConfigValue(data, ["BRAND_MUTED_COLOR", "BRAND_SECONDARY_COLOR"]),
        cta: getFirstFrontConfigValue(data, ["BRAND_CTA_COLOR", "BRAND_ACTION_COLOR"]),
        background: getFirstFrontConfigValue(data, ["BRAND_BACKGROUND_COLOR", "BRAND_PAGE_BACKGROUND_COLOR"]),
        surface: getFirstFrontConfigValue(data, ["BRAND_SURFACE_COLOR", "BRAND_CARD_COLOR"]),
        text: getFirstFrontConfigValue(data, ["BRAND_TEXT_COLOR", "BRAND_HEADING_COLOR"]),
        muted: getFirstFrontConfigValue(data, ["BRAND_MUTED_TEXT_COLOR", "BRAND_SUPPORTING_COLOR"]),
        dark: getFirstFrontConfigValue(data, ["BRAND_DARK_COLOR", "BRAND_FOOTER_COLOR"]),
        onPrimary: getFirstFrontConfigValue(data, ["BRAND_ON_PRIMARY_COLOR", "BRAND_PRIMARY_TEXT_COLOR"]),
        onCta: getFirstFrontConfigValue(data, ["BRAND_ON_CTA_COLOR", "BRAND_CTA_TEXT_COLOR"]),
      };
      const frontAssets = {
        background: getFirstFrontConfigValue(data, [
          "APP_DOWNLOAD_BACKGROUND",
          "APP_BACKGROUND",
          "APP_BG",
        ]),
        screen1: getFirstFrontConfigValue(data, [
          "APP_DOWNLOAD_SCREEN_1",
          "APP_SCREEN_1",
          "APP1",
        ]),
        screen2: getFirstFrontConfigValue(data, [
          "APP_DOWNLOAD_SCREEN_2",
          "APP_SCREEN_2",
          "APP2",
        ]),
        screen3: getFirstFrontConfigValue(data, [
          "APP_DOWNLOAD_SCREEN_3",
          "APP_SCREEN_3",
          "APP3",
        ]),
        appStoreBadge: getFirstFrontConfigValue(data, ["APPSTORE_BADGE", "APP_STORE_BADGE"]),
        playStoreBadge: getFirstFrontConfigValue(data, [
          "PLAYSTORE_BADGE",
          "PLAY_STORE_BADGE",
          "GOOGLEPLAY_BADGE",
        ]),
      };

      const defaultLanguageConfig = getFrontConfigValue(data, "LANGUAGE");

      if (defaultLanguageConfig) setDefaultLanguageValue((prev) => prev || defaultLanguageConfig);
      const safeAppUrl = normalizeHttpsUrl(appUrl);
      const safePlayUrl = normalizeHttpsUrl(playUrl);
      if (safeAppUrl) setAppStoreUrl((prev) => prev || safeAppUrl);
      if (safePlayUrl) setPlayStoreUrl((prev) => prev || safePlayUrl);
      setBrandTheme((prev) => normalizeBrandTheme(frontTheme, prev));
      setAppDownloadImages((prev) => {
        const next = { ...prev };
        Object.keys(frontAssets).forEach((key) => {
          if (frontAssets[key]) {
            next[key] = toMediaUrl(frontAssets[key]);
          }
        });
        return next;
      });
    }

    async function loadStoreLinks() {
      try {
        const cachedFrontConfig = getStoredFrontConfig();
        if (mounted) applyFrontConfig(cachedFrontConfig);

        const data = await loadAuthenticatedFrontConfig();
        if (!mounted) return;

        applyFrontConfig(data);
      } catch (e) {
        // ignore: store links have page-level fallbacks
      }
    }

    loadStoreLinks();
    return () => {
      mounted = false;
    };
  }, []);

  const translatedProducts = useMemo(
    () =>
      (products || []).map((product) =>
        applyEntityTranslation(product, "pt_product", selectedLanguage, defaultLanguage)
      ),
    [products, selectedLanguage, defaultLanguage]
  );

  const translatedCategories = useMemo(
    () =>
      (categories || []).map((category) => {
        const translatedCategory = applyEntityTranslation(
          category,
          "pct_product",
          selectedLanguage,
          defaultLanguage
        );

        return {
          ...translatedCategory,
          subCategory: (category.subCategory || []).map((subCategory) =>
            applyEntityTranslation(
              subCategory,
              "psbt_product",
              selectedLanguage,
              defaultLanguage
            )
          ),
        };
      }),
    [categories, selectedLanguage, defaultLanguage]
  );

  const translatedAllergens = useMemo(
    () =>
      (allergens || []).map((allergen) => {
        const translated = getAllergenTranslation(allergen, selectedLanguage, defaultLanguage);
        const iconPath = translated.image || translated.image_off || "";

        return {
          ...translated,
          iconUrl: iconPath ? toMediaUrl(iconPath) : "",
        };
      }),
    [allergens, selectedLanguage, defaultLanguage]
  );

  const brandName = useMemo(
    () => getTextConfigValue(mobileTextConfigs.BRAND_NAME, "", selectedLanguage, defaultLanguage),
    [mobileTextConfigs, selectedLanguage, defaultLanguage]
  );

  const brandTagline = useMemo(
    () => getTextConfigValue(mobileTextConfigs.BRAND_TAGLINE, "", selectedLanguage, defaultLanguage),
    [mobileTextConfigs, selectedLanguage, defaultLanguage]
  );

  const websiteContent = useMemo(
    () =>
      buildWebsiteContent({
        configsByKey: mobileTextConfigs,
        selectedLanguage,
        defaultLanguage,
        bannerUrl,
        images: websiteImages,
      }),
    [
      mobileTextConfigs,
      selectedLanguage,
      defaultLanguage,
      bannerUrl,
      websiteImages,
    ]
  );

  const localizedAppDownloadImages = useMemo(
    () => resolveLocalizedStoreBadges(appDownloadImages, languageCode),
    [appDownloadImages, languageCode]
  );

  useEffect(() => {
    const title = String(brandName || "").trim();
    if (title) document.title = title;
  }, [brandName]);

  useEffect(() => {
    if (!logoUrl) return undefined;

    const favicon = document.querySelector('link[rel~="icon"]');
    if (!favicon) return undefined;

    const fallbackHref = favicon.getAttribute("href");
    favicon.setAttribute("href", logoUrl);

    return () => {
      if (fallbackHref === null) {
        favicon.removeAttribute("href");
      } else {
        favicon.setAttribute("href", fallbackHref);
      }
    };
  }, [logoUrl]);

  const publicContentError =
    publicTextConfigError || publicImageConfigError;

  const value = useMemo(() => {
    const getProductBySlug = (slug) =>
      (translatedProducts || []).find((p) => String(p.slug || p.iref) === String(slug));

    const getLocationByIref = (iref) =>
      (locations || []).find((l) => String(l.iref) === String(iref));

    const getCategoryName = (id) => {
      const c = (translatedCategories || []).find((x) => String(x.iref) === String(id));
      return c ? c.shortName || c.name : "";
    };

    const getProductAllergens = (product) =>
      resolveProductAllergens(product, translatedAllergens);

    return {
      loading,
      error,
      publicConfigReady,
      publicImagesReady,
      publicContentError,
      languageReady,

      products: translatedProducts,
      locations,
      categories: translatedCategories,
      allergens: translatedAllergens,

      logoUrl,
      bannerUrl,
      brandName,
      brandTagline,
      appStoreUrl,
      playStoreUrl,
      appDownloadImages: localizedAppDownloadImages,
      websiteContent,
      brandTheme,
      languages,
      selectedLanguage,
      selectedLanguageIref: getLanguageIref(selectedLanguage),
      defaultLanguage,
      languageCode,
      setSelectedLanguageIref,
      t,
      retryCore,
      retryPublicContent,

      // lookups
      getProductBySlug,
      getLocationByIref,
      getCategoryName,
      getProductAllergens,
    };
  }, [
    loading,
    error,
    publicConfigReady,
    publicImagesReady,
    publicContentError,
    languageReady,
    translatedProducts,
    locations,
    translatedCategories,
    translatedAllergens,
    logoUrl,
    bannerUrl,
    brandName,
    brandTagline,
    appStoreUrl,
    playStoreUrl,
    localizedAppDownloadImages,
    websiteContent,
    brandTheme,
    languages,
    selectedLanguage,
    defaultLanguage,
    languageCode,
    setSelectedLanguageIref,
    t,
    retryCore,
    retryPublicContent,
  ]);

  return <DataStoreContext.Provider value={value}>{children}</DataStoreContext.Provider>;
}

export function useDataStore() {
  const ctx = useContext(DataStoreContext);
  if (!ctx) throw new Error("useDataStore must be used inside DataStoreProvider");
  return ctx;
}
